import { callFunction, FunctionsError } from "./functions-client";

// Typed calls to the `verification` edge function group (ADR-0012).

export interface CredentialStatus {
  id: string;
  profile_id: string;
  doc_type: string;
  status: string;
  decision_reason: string | null;
  created_at: string;
}

export interface AuthorityVerification {
  profile_id: string;
  dot_number: string | null;
  mc_number: string | null;
  status: "not_checked" | "active" | "inactive" | "not_found" | "error";
  allowed_to_operate: boolean | null;
  legal_name: string | null;
  entity_type: string | null;
  safety_rating: string | null;
  checked_at: string | null;
  display_name?: string | null;
  profile_type?: string;
}

export interface VerificationStatus {
  identity: { status: string };
  credentials: CredentialStatus[];
  authority: AuthorityVerification[];
}

export interface QueueItem {
  id: string;
  profile_id: string;
  doc_type: string;
  status: string;
  profile_type: string;
  display_name: string | null;
  account_id: string;
  document_url: string | null;
  created_at: string;
  decision_reason: string | null;
  account_email: string | null;
  claimed: Record<string, unknown> | null;
}

export const getVerificationStatus = () =>
  callFunction<VerificationStatus>("verification/status", { method: "GET" });

export const submitCredential = (profile_id: string, doc_type: string) =>
  callFunction<{ submission_id: string; upload_url: string | null; path: string }>(
    "verification/submit",
    { body: { profile_id, doc_type } },
  );

export const startIdentity = () =>
  callFunction<{ client_secret?: string; url?: string }>("verification/start", { body: {} });

/** Webhook-independent reconcile: pull the caller's Stripe Identity session status + apply it. */
export const syncIdentity = () =>
  callFunction<{ status: string }>("verification/sync", { body: {} });

/** Check a company/broker profile's FMCSA DOT/MC operating authority (ADR-0009). */
export const checkAuthority = (profile_id: string) =>
  callFunction<{ authority: AuthorityVerification }>("verification/authority-check", { body: { profile_id } });

export const adminQueue = (status = "pending") =>
  callFunction<{ queue: QueueItem[]; counts: Record<string, number> }>(`verification/queue?status=${encodeURIComponent(status)}`, { method: "GET" });

export const adminReview = (submission_id: string, decision: string, reason?: string) =>
  callFunction<{ ok: boolean; status: string }>("verification/review", {
    body: { submission_id, decision, reason },
  });

/** True when a failed call was a 501 (feature not yet configured, e.g. Stripe). */
export const isNotConfigured = (e: unknown) => e instanceof FunctionsError && e.status === 501;

/** Upload a document to a Supabase signed upload URL (the token is in the URL). */
export async function uploadToSignedUrl(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "content-type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}
